import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { 
  DashboardSummary, 
  ChartData, 
  TopModelData, 
  CampaignMetrics, 
  AnalyticsTableResponse,
  
} from '../types/api';

// Типы для кэширования
export interface CachedData {
  summary?: DashboardSummary;
  spendsChartData?: ChartData;
  ctrChartData?: ChartData;
  topModels?: TopModelData[];
  campaignDetails?: CampaignMetrics;
  analyticsTable?: AnalyticsTableResponse;
  timestamp: number; // Временная метка в миллисекундах
}

export interface AnalyticsCacheState {
  // Кэш по аккаунтам
  byAccount: {
    [accountName: string]: CachedData;
  };
  // Время жизни кэша (в миллисекундах) - 30 минут по умолчанию
  cacheTTL: number;
}

const initialState: AnalyticsCacheState = {
  byAccount: {},
  cacheTTL: 30 * 60 * 1000, // 30 минут
};

export const analyticsCacheSlice = createSlice({
  name: 'analyticsCache',
  initialState,
  reducers: {
    // Сохранение кэшированных данных для аккаунта
    setCachedData: (
      state,
      action: PayloadAction<{ accountName: string; data: Partial<CachedData> }>
    ) => {
      const { accountName, data } = action.payload;
      
      if (!state.byAccount[accountName]) {
        state.byAccount[accountName] = {
          timestamp: Date.now(),
          ...data
        };
      } else {
        // Обновляем существующие данные
        Object.assign(state.byAccount[accountName], data, { timestamp: Date.now() });
      }
    },

    // Удаление кэшированных данных для аккаунта
    removeCachedData: (
      state,
      action: PayloadAction<{ accountName: string }>
    ) => {
      delete state.byAccount[action.payload.accountName];
    },

    // Удаление устаревших данных
    cleanupExpiredCache: (state) => {
      const now = Date.now();
      const expiredAccounts = Object.keys(state.byAccount).filter(
        account => now - state.byAccount[account].timestamp > state.cacheTTL
      );

      expiredAccounts.forEach(account => {
        delete state.byAccount[account];
      });
    },

    // Установка времени жизни кэша
    setCacheTTL: (state, action: PayloadAction<number>) => {
      state.cacheTTL = action.payload;
    },

    // Полная очистка кэша
    clearCache: (state) => {
      state.byAccount = {};
    }
  },
});

export const {
  setCachedData,
  removeCachedData,
  cleanupExpiredCache,
  setCacheTTL,
  clearCache
} = analyticsCacheSlice.actions;

export default analyticsCacheSlice.reducer;